/**
 * **The language is the person's, and choosing one is done once it is saved** (F344; Thomas, 2026-10-09: *F344: fix
 * it in the app too. The language switch navigates only once the save has answered.*, and *Language per person,
 * not per profile.*).
 *
 * Choosing a language used to switch the screen at once and save it a request later. A full load then read the
 * saved language and applied it over the screen's, so a reload — or one of the app's own full loads — before the
 * save answered brought the old language back, and the choice was gone without a word. Now:
 *
 * - the screen keeps its language while the choice is saved, and the control says it is saving;
 * - only the save's answer switches it: a saved choice switches the screen, a refused one goes back and says why;
 * - the choice is written down in this browser before it is sent, and sent with `keepalive`, so a load that finds
 *   one still written down sends it again before anything else decides the language. A reload mid-save ends in
 *   the language chosen, or says why it could not be saved — never silently in the old one. Only a recent one is
 *   sent again (`RESEND_WITHIN_MS`): one left by a page closed long ago is not allowed to undo a later choice;
 * - the language applied on a load is the person's (`GET /api/me`'s `locale`), and only when it changes: opening
 *   another profile never changes it.
 *
 * Rendered by the Gate above the routes, so a navigation inside the app never interrupts a save.
 */
import type { Schemas } from '@autho/api-client';
import type { Locale } from '@autho/i18n';
import { isLocale } from '@autho/i18n';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ErrorNote } from '../pages/common.tsx';
import { keys, useSaveLanguage } from './api.ts';
import { usePreferences, useT } from './preferences.tsx';

/** Where a choice waits between being made and being saved. Per browser, like the screen's own language. */
const SAVING = 'autho.locale.saving';

/**
 * **How long a choice written down is sent again.** A reload in the middle of a save comes back within seconds. A
 * choice found much later is from a page closed mid-save — whose save most likely went through, being sent with
 * `keepalive` — and sending it again could undo a language chosen since, on another device.
 */
export const RESEND_WITHIN_MS = 10 * 60_000;

/** How long after asking to leave a page that is still here is taken to have stayed. */
const STAYED_MS = 2_000;

/**
 * The choice written down, as storage holds it: the language, when it was chosen and by whom. Another person's —
 * one who signed out of this browser before their save answered — is not theirs to send for this one.
 */
export function writtenChoice(raw: string | null, now: number, person: string): Locale | null {
  if (raw === null) return null;
  try {
    const { locale, at, by } = JSON.parse(raw) as { locale?: unknown; at?: unknown; by?: unknown };
    return isLocale(locale) &&
      by === person &&
      typeof at === 'number' &&
      now - at >= 0 &&
      now - at < RESEND_WITHIN_MS
      ? locale
      : null;
  } catch {
    return null;
  }
}

function written(person: string): Locale | null {
  try {
    const raw = window.localStorage.getItem(SAVING);
    const choice = writtenChoice(raw, Date.now(), person);
    // One too old to send is dropped, so it is not found again on the next load either.
    if (raw !== null && choice === null) window.localStorage.removeItem(SAVING);
    return choice;
  } catch {
    return null;
  }
}

function write(locale: Locale | null, person: string) {
  try {
    if (locale === null) window.localStorage.removeItem(SAVING);
    else
      window.localStorage.setItem(SAVING, JSON.stringify({ locale, at: Date.now(), by: person }));
  } catch {
    // Storage can be unavailable (private mode): the choice then lasts as long as this page does.
  }
}

interface Language {
  /** The language a choice is being saved as, until the save has answered. */
  saving: Locale | null;
  /** Why the last choice could not be saved, until the next one is made. */
  refused: unknown;
  /** Saves the choice, and switches the screen once it is saved. Ignored while a save is in flight. */
  choose: (locale: Locale) => void;
}

const LanguageContext = createContext<Language | null>(null);

export function useLanguage(): Language {
  const language = useContext(LanguageContext);
  if (!language) throw new Error('useLanguage outside LanguageProvider');
  return language;
}

/** A choice that could not be saved: what that means for the screen, and the reason as the answer gave it. */
export function LanguageNotSaved({ error }: { error: unknown }) {
  const t = useT();
  return (
    <div data-testid="language-not-saved">
      <p className="text-sm text-danger">{t('settings.language.notSaved')}</p>
      <ErrorNote error={error} />
    </div>
  );
}

export function LanguageProvider({
  person,
  saved,
  children,
}: {
  /** Who is signed in: a choice is written down as theirs, and only theirs is sent again. */
  person: string;
  /** The person's saved language, as `GET /api/me` says it. */
  saved: Locale | undefined;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const { locale, setLocale } = usePreferences();
  const save = useSaveLanguage();
  const [saving, setSaving] = useState<Locale | null>(() => written(person));
  const [refused, setRefused] = useState<unknown>(null);
  /** A save still to be resumed from before this load: said here, because no control on the page asked for it. */
  const [resumeRefused, setResumeRefused] = useState<unknown>(null);
  const inFlight = useRef(false);
  const { mutate } = save;

  /**
   * **A page going away is no answer.** A reload, or a full load of the app's own, can cut off the save it was
   * waiting for, and the request then fails in this page's last moments. That failure says nothing about the
   * choice: it stays written down, and the page that loads next sends it again. Set from the first sign the page is
   * leaving, and cleared if it comes back from the browser's cache instead — or if it is still here a moment later:
   * a page can ask to leave and stay, as one does for a download, and a page that stays is not leaving.
   */
  const leaving = useRef(false);
  useEffect(() => {
    let stayed: number | undefined;
    const away = () => {
      leaving.current = true;
      window.clearTimeout(stayed);
      stayed = window.setTimeout(() => {
        leaving.current = false;
      }, STAYED_MS);
    };
    const back = (event: PageTransitionEvent) => {
      if (event.persisted) leaving.current = false;
    };
    window.addEventListener('beforeunload', away);
    window.addEventListener('pagehide', away);
    window.addEventListener('pageshow', back);
    return () => {
      window.clearTimeout(stayed);
      window.removeEventListener('beforeunload', away);
      window.removeEventListener('pagehide', away);
      window.removeEventListener('pageshow', back);
    };
  }, []);

  /**
   * What a save's failure and its end do to the screen, once it is plain whether the page stayed: at once, or — when
   * the page has said it is leaving — a moment after it would have stopped leaving. A page that really leaves never
   * gets there, and its choice stays written down; one that stayed gets there as it would have.
   */
  const onceStayed = useCallback((then: () => void) => {
    if (!leaving.current) then();
    else window.setTimeout(then, STAYED_MS + 500);
  }, []);

  /**
   * Sends a choice, written down first so a reload in the middle sends it again. What the answer does is done in
   * the save's own callbacks: the screen switches on a saved choice and on nothing else.
   */
  const send = useCallback(
    (next: Locale, resumed: boolean) => {
      inFlight.current = true;
      write(next, person);
      mutate(next, {
        onSuccess: () => {
          setLocale(next);
          // The person's language as the server now holds it, before anything reads it again.
          qc.setQueryData<Schemas['Me']>(keys.me, (me) =>
            me === undefined
              ? me
              : {
                  ...me,
                  locale: next,
                  memberships: me.memberships.map((m) => ({ ...m, locale: next })),
                },
          );
          void qc.invalidateQueries({ queryKey: keys.me });
        },
        onError: (error) =>
          onceStayed(() => {
            if (resumed) setResumeRefused(error);
            else setRefused(error);
          }),
        onSettled: () =>
          onceStayed(() => {
            inFlight.current = false;
            write(null, person);
            setSaving(null);
          }),
      });
    },
    [mutate, qc, setLocale, person, onceStayed],
  );

  const choose = useCallback(
    (next: Locale) => {
      if (inFlight.current) return;
      setRefused(null);
      setResumeRefused(null);
      setSaving(next);
      send(next, false);
    },
    [send],
  );

  // A choice still written down from before this load — `saving` starts as it — sent again, once, before the
  // saved language is applied.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current) return;
    resumed.current = true;
    if (saving !== null) send(saving, true);
  }, [send, saving]);

  // The person's saved language wins over the browser's default once known — when it changes, never because a
  // profile was opened, and never over a choice still being saved.
  useEffect(() => {
    if (saved !== undefined && saving === null && saved !== locale) setLocale(saved);
    // Only the saved language moves the screen here; a change of the screen's own language is no reason to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const value = useMemo(() => ({ saving, refused, choose }), [saving, refused, choose]);
  return (
    <LanguageContext.Provider value={value}>
      {resumeRefused !== null && (
        <div className="border-b border-border bg-surface px-4 py-2">
          <LanguageNotSaved error={resumeRefused} />
        </div>
      )}
      {children}
    </LanguageContext.Provider>
  );
}
